import { Children, isValidElement, ReactElement } from 'react';

import { Fn, Predicate } from '@/types/common';
import { ElementPropName, FindElementPredicate } from '@/types/ssr';
import { curry, equals, includes, isEmpty, isString, pipe, prop } from '@/utils/common';

export const makeElementTypeEqual: Fn<string, FindElementPredicate> = elementType =>
  pipe(prop('type'), equals(elementType));

export const elementPropIs = <V>(pred: Predicate<V[]>, prop: ElementPropName, value: V, element: ReactElement) =>
  pred(element?.props?.[prop], value);

export const curriedElementPropEq = curry(elementPropIs)(equals);

export const classNameIs = curriedElementPropEq('className');

export const curriedElementPropIncludes = curry(elementPropIs<string>)(includes);

export const classNameIncludes = curriedElementPropIncludes('className');

export const isCaptionElement = makeElementTypeEqual('caption');
export const isColElement = makeElementTypeEqual('col');
export const isTrElement = makeElementTypeEqual('tr');
export const isDtElement = makeElementTypeEqual('dt');
export const isTheadElement = makeElementTypeEqual('thead');
export const isTbodyElement = makeElementTypeEqual('tbody');

export const findElements = <T extends ReactElement>(elements: T[], predicate: Predicate<T[]>): T[] => {
  const getChildrenArray = (children: T | T[] | undefined) => {
    const fallback: T[] = [];
    if (!children) return fallback;
    if (Array.isArray(children)) return children;
    if (isValidElement(children)) return [children];
    return fallback;
  };

  return elements.reduce<T[]>((acc, element) => {
    if (predicate(element)) {
      acc.push(element);
    }

    const children = getChildrenArray(element?.props?.children);
    if (!isEmpty(children)) {
      acc.push(...findElements(children, predicate));
    }

    return acc;
  }, []);
};

export const collectTextContent = (element: ReactElement | ReactElement[]): string[] => {
  const results: string[] = [];

  const processNode = (node: ReactElement | string | number | boolean | null | undefined) => {
    if (isString(node)) {
      results.push(node);
    } else if (equals(typeof node, 'number')) {
      results.push(String(node));
    } else if (isValidElement(node)) {
      const children = (node as ReactElement).props.children;

      if (children) {
        Children.forEach(children, child => {
          if (isValidElement(child)) {
            results.push(...collectTextContent(child));
          } else if (isString(child) || equals(typeof child, 'number')) {
            results.push(String(child));
          }
        });
      }
    }
  };

  Children.forEach(Array.isArray(element) ? element : [element], processNode);

  return results.filter(Boolean);
};
